import moment from 'moment';
import {
  FormResponse,
  QuestionType,
  QuestionResponseMetadata,
  RATING_ICONS,
} from '../../types';
import FilePreview from '../QuestionsTab/FilePreview';
import RenderFiles from '../../../../../operations/components/Modals/AddAnnouncement/components/RenderFiles';
import AppSingleSlider from '../../../../../../components/common/AppSingleSlider';
import AppRangeSlider from '../../../../../../components/common/AppRangeSlider';
import {
  clampInRange,
  formatRangeEndpointLabel,
  getNumericRangeBounds,
  getTextualLabels,
} from '../../../../../public-form/components/QuestionTypes/PublicRange';

type Props = {
  response: FormResponse;
  questionType: QuestionType;
  metadata?: QuestionResponseMetadata;
  isResponseByUserCard?: boolean;
  question?: string;
};

/** Get filled / empty icon paths for a rating icon type */
function getRatingIcons(iconType: string) {
  const match = RATING_ICONS.find(i => i.value === iconType);
  return {
    filled: match?.dropdownIcon,
    empty: match?.emptyIcon,
  };
}

// --------------- Answer renderers per question type ---------------

function TimeAnswer({answer}: {answer: {startTime: string; endTime?: string}}) {
  let formatted = '';
  if (answer?.startTime && answer?.endTime)
    formatted = `${answer?.endTime} - ${answer?.startTime}`;
  else formatted = answer?.startTime;
  return (
    <div>
      <span className="bg-pastelBlue text-black px-3 py-1 rounded-lg text-xl font-bold">
        {formatted}
      </span>
    </div>
  );
}

function DateAnswer({answer}: {answer: {startDate: string; endDate?: string}}) {
  const start = moment(answer?.startDate);
  const end = moment(answer?.endDate);

  let formatted = '';
  if (answer?.startDate && answer?.endDate)
    formatted = `${start.format('dddd')} - ${end.format('dddd')} ${end.format('DD/MM/YYYY')} - ${start.format('DD/MM/YYYY')}`;
  else formatted = start.format('dddd DD/MM/YYYY');
  return (
    <div>
      <span className="bg-pastelBlue text-black px-3 py-1 rounded-lg text-xl font-bold">
        {formatted}
      </span>
    </div>
  );
}

function ShortAnswer({answer}: {answer: string}) {
  return (
    <div className="bg-lightPastelBlue text-darkerGray px-3 py-1 rounded-lg text-base font-bold w-fit">
      {/* <span className="bg-pastelBlue text-black px-3 py-1 rounded-lg text-xl font-bold"> */}
      {answer}
      {/* </span> */}
    </div>
  );
}

function MultipleChoiceAnswer({
  answer,
}: {
  answer: {isOther?: boolean; label?: string; input?: string; image?: string};
}) {
  return (
    <div>
      {(answer?.label || answer?.input) && (
        <span className="bg-lightPastelBlue text-darkerGray px-3 py-1 rounded-lg text-base font-bold">
          {answer?.label || answer?.input}
        </span>
      )}
      {answer?.image && (
        <div className="mt-4">
          <FilePreview file={answer.image} />
        </div>
      )}
    </div>
  );
}

function CheckboxAnswer({
  answer,
}: {
  answer: {
    isOther?: boolean;
    label?: string;
    input?: string;
    image?: string;
  }[];
}) {
  return (
    <div className="flex flex-col gap-2">
      {answer?.map((item, idx) => (
        <div key={idx}>
          {(item?.label || item?.input) && (
            <span className="bg-lightPastelBlue text-darkerGray px-3 py-1 rounded-lg text-base font-bold">
              {item?.label || item?.input}
            </span>
          )}
          {item?.image && (
            <div className="mt-4">
              <FilePreview file={item.image} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function LinearScaleAnswer({
  answer,
  metadata,
}: {
  answer: string;
  metadata?: QuestionResponseMetadata;
}) {
  const value = parseInt(answer, 10);
  const from = metadata?.fromValue ?? 1;
  const to = metadata?.toValue ?? 10;
  const fromLabel = metadata?.fromLabel ?? 'Low';
  const toLabel = metadata?.toLabel ?? 'High';
  const range = Array.from({length: to - from + 1}, (_, i) => from + i);

  return (
    <div className="w-full">
      <div className="flex items-center gap-6">
        {/* Left Label */}
        <span className="text-base text-gray-600 whitespace-nowrap">
          {toLabel}
        </span>

        {/* Scale Numbers */}
        <div className="flex items-center flex-row-reverse gap-6">
          {range.map(number => {
            const isChecked = value === number;

            return (
              <div
                key={number}
                className={`flex flex-col items-center gap-3 px-2 py-1 rounded-lg transition-all
                  ${isChecked ? 'bg-lightPastelBlue' : ''}
                `}>
                <span className="text-sm font-medium">{number}</span>

                <input
                  type="radio"
                  checked={isChecked}
                  disabled
                  className="w-5 h-5 accent-blue-600"
                />
              </div>
            );
          })}
        </div>

        {/* Right Label */}
        <span className="text-base text-gray-600 whitespace-nowrap">
          {fromLabel}
        </span>
      </div>
    </div>
  );
}

type FileUploadAnswerItem = {url: string; type?: string};

function getUrlExtension(url: string): string {
  const name = url.split('/').pop()?.split('?')[0] || '';
  return (name.split('.').pop() || '').toLowerCase();
}

function parseFileUploadAnswer(value: unknown): FileUploadAnswerItem[] {
  if (value == null) return [];
  let raw: unknown = value;
  if (typeof value === 'string') {
    try {
      raw = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  return raw
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const o = item as Record<string, unknown>;
      const url = typeof o.url === 'string' ? o.url : '';
      if (!url) return null;
      const type = typeof o.type === 'string' ? o.type : undefined;
      return {url, type};
    })
    .filter(Boolean) as FileUploadAnswerItem[];
}

function fileUploadItemIsImage(file: FileUploadAnswerItem): boolean {
  const ft = (file.type || '').toLowerCase();
  const ext = getUrlExtension(file.url);
  return (
    ft === 'image' ||
    ft.startsWith('image/') ||
    ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'].includes(ext)
  );
}

function fileUploadItemIsVideo(file: FileUploadAnswerItem): boolean {
  const ft = (file.type || '').toLowerCase();
  const ext = getUrlExtension(file.url);
  return (
    ft === 'video' ||
    ft.startsWith('video/') ||
    ['mp4', 'mov', 'webm'].includes(ext)
  );
}

function FileUploadAnswer({answer}: {answer: unknown}) {
  const files = parseFileUploadAnswer(answer);
  if (files.length === 0) {
    return (
      <span className="bg-lightPastelBlue text-darkerGray px-3 py-1 rounded-lg text-base font-bold w-fit">
        —
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {files.map((file, idx) => {
        const key = `${file.url}-${idx}`;
        if (fileUploadItemIsImage(file) || fileUploadItemIsVideo(file)) {
          return (
            <div key={key}>
              <FilePreview file={file.url} />
            </div>
          );
        }
        const mimeFromCategory = (() => {
          const t = (file.type || '').toLowerCase();
          if (t === 'image') return 'image/*';
          if (t === 'video') return 'video/*';
          if (t === 'audio') return 'audio/*';
          if (t === 'document') return 'application/octet-stream';
          return file.type || '';
        })();
        return (
          <RenderFiles
            key={key}
            file={{url: file.url, type: mimeFromCategory}}
            index={idx}
            showRemoveIcon={false}
          />
        );
      })}
    </div>
  );
}

function RatingAnswer({
  answer,
  metadata,
}: {
  answer: string;
  metadata?: QuestionResponseMetadata;
}) {
  const value = parseInt(answer, 10);
  const max = metadata?.maxRating ?? 5;
  const iconType = metadata?.ratingIcon ?? 'star';
  const icons = getRatingIcons(iconType);

  return (
    <div className="w-full">
      <div className="flex">
        <div className="flex items-center flex-row-reverse gap-6">
          {Array.from({length: max}).map((_, idx) => (
            <div
              key={idx}
              className="flex flex-col items-center gap-3 px-2 py-1 rounded-lg transition-all">
              <span className="text-sm font-medium">{idx + 1}</span>
              <img
                src={idx < value ? icons.filled : icons.empty}
                alt=""
                className="w-5 h-5 object-contain"
              />
            </div>
          ))}
        </div>
      </div>
      {metadata?.qImage && (
        <div className="mt-4">
          <FilePreview file={metadata.qImage} />
        </div>
      )}
    </div>
  );
}

function RangeAnswer({
  answer,
  metadata,
}: {
  answer: {
    image?: string;
    value?: number | string;
    from?: number;
    to?: number;
  };
  metadata?: QuestionResponseMetadata;
}) {
  const cfg = metadata?.qRange;
  const rangeType = cfg?.rangeType;
  const formType = cfg?.form;

  const isNumericLike = rangeType === 'numeric' || rangeType === 'temperature';
  const isTemperature = rangeType === 'temperature';
  const isTextual = rangeType === 'textual';
  const isDualSlider = formType === 'range';

  const [numericSafeMin, numericSafeMax] = getNumericRangeBounds(cfg);

  const [fromTextualLabels, toTextualLabels] = getTextualLabels(
    cfg?.minLabel,
    cfg?.maxLabel,
  );

  const formatEndLabel = (v: number) =>
    formatRangeEndpointLabel(v, isTemperature);

  const image = answer?.image ?? '';

  const dualSliderValue: [number, number] = (() => {
    const rawFrom = answer?.from;
    const rawTo = answer?.to;
    if (typeof rawFrom === 'number' && typeof rawTo === 'number') {
      const lo = Math.min(rawFrom, rawTo);
      const hi = Math.max(rawFrom, rawTo);
      return [
        clampInRange(lo, numericSafeMin, numericSafeMax),
        clampInRange(hi, numericSafeMin, numericSafeMax),
      ];
    }
    return [numericSafeMin, numericSafeMax];
  })();

  const singleSliderValue: number = (() => {
    const raw = answer?.value;
    if (isTextual) {
      if (raw === undefined || raw === '') return 50;
      const n = Number(raw);
      return Number.isFinite(n) ? clampInRange(n, 0, 100) : 50;
    }
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return clampInRange(raw, numericSafeMin, numericSafeMax);
    }
    if (typeof raw === 'string' && raw !== '') {
      const n = Number(raw);
      if (Number.isFinite(n))
        return clampInRange(n, numericSafeMin, numericSafeMax);
    }
    return Math.round((numericSafeMin + numericSafeMax) / 2);
  })();

  const sliderReadOnly = {disabled: true as const};

  return (
    <div className="mt-5 flex flex-col gap-4">
      {isDualSlider && isNumericLike && (
        <AppRangeSlider
          value={dualSliderValue}
          valueLabelDisplay="on"
          disableSwap
          min={numericSafeMin}
          max={numericSafeMax}
          valueLabelFormat={
            isTemperature ? v => formatRangeEndpointLabel(v, true) : undefined
          }
          startLabel={formatEndLabel(numericSafeMin)}
          endLabel={formatEndLabel(numericSafeMax)}
          containerClassName="w-[430px]"
          {...sliderReadOnly}
          sx={{
            '& .MuiSlider-track': {height: 4, border: 'none'},
            '& .MuiSlider-rail': {
              height: 4,
              opacity: 1,
              backgroundColor: '#E6E0E9',
            },
          }}
        />
      )}

      {!isDualSlider && isNumericLike && (
        <AppSingleSlider
          value={singleSliderValue}
          valueLabelDisplay="on"
          min={numericSafeMin}
          max={numericSafeMax}
          valueLabelFormat={
            isTemperature ? v => formatRangeEndpointLabel(v, true) : undefined
          }
          startLabel={formatEndLabel(numericSafeMin)}
          endLabel={formatEndLabel(numericSafeMax)}
          containerClassName="w-[220px]"
          {...sliderReadOnly}
        />
      )}

      {isTextual && (
        <AppSingleSlider
          value={singleSliderValue}
          valueLabelDisplay="off"
          min={0}
          max={100}
          startLabel={fromTextualLabels}
          endLabel={toTextualLabels}
          containerClassName="w-[220px]"
          {...sliderReadOnly}
        />
      )}

      {image ? <FilePreview file={image} /> : null}
    </div>
  );
}

// --------------- Main component ---------------

function ResponseCard({
  response,
  questionType,
  metadata,
  isResponseByUserCard = false,
  question,
}: Props) {
  const formattedDate = moment(response?.respondedAt).format('DD/MM/YYYY');
  const formattedTime = moment(response?.respondedAt).format('HH:mm');

  const renderAnswer = () => {
    switch (questionType) {
      case 'time':
        return <TimeAnswer answer={response?.value} />;
      case 'date':
        return <DateAnswer answer={response?.value} />;
      case 'short-answer':
        return <ShortAnswer answer={response?.value} />;
      case 'multiple-choice':
      case 'dropdown':
        return <MultipleChoiceAnswer answer={response?.value} />;
      case 'checkbox':
        return <CheckboxAnswer answer={response?.value} />;
      case 'linear-scale':
        return (
          <LinearScaleAnswer answer={response.value} metadata={metadata} />
        );
      case 'rating':
        return <RatingAnswer answer={response.value} metadata={metadata} />;
      case 'file-upload':
        return <FileUploadAnswer answer={response?.value} />;
      case 'range':
        return <RangeAnswer answer={response?.value} metadata={metadata} />;
      default:
        return (
          <span className="bg-pastelBlue text-black px-3 py-1 rounded-md text-sm font-medium"></span>
        );
    }
  };

  return (
    <div className="rounded-lg shadow-md p-3 border border-gray70">
      <div className="flex flex-col gap-2">
        {isResponseByUserCard && question ? (
          <div className="text-lg font-bold mt-2">{question}</div>
        ) : (
          <div className="flex items-center gap-2">
            {/* Responder info */}
            <span>
              {formattedDate} {formattedTime}
            </span>

            {response?.responder?.name && (
              <>
                <div className="w-[1px] h-[14px] bg-gray70" />
                <span className="text-black font-bold">
                  {response?.responder?.name}
                </span>
              </>
            )}

            {response?.responder?.email && (
              <>
                <div className="w-[1px] h-[14px] bg-gray70" />
                <a
                  href={`mailto:${response?.responder?.email}`}
                  className="text-linkBlue">
                  {response?.responder?.email}
                </a>
              </>
            )}
          </div>
        )}
        {/* Answer */}
        <div>{renderAnswer()}</div>
      </div>
    </div>
  );
}

export default ResponseCard;
